import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Button, Typography, Link, Checkbox, Box } from '@mui/material';
import ZoomableImage from './ZoomableImage';
import debounce from 'lodash/debounce';
import PaginationControls from './PaginationControls';
import { getStatusColor } from './utils';

// Helper function to group data into pages
const getPaginatedData = (data, rowsPerPage) => {
  let groupedData = [];
  let currentGroup = [];

  data.forEach(item => {
    if (currentGroup.length === 0) {
      currentGroup.push(item);
    } else if (item["Group field"] === currentGroup[0]["Group field"]) {
      currentGroup.push(item);
    } else {
      groupedData.push(currentGroup);
      currentGroup = [item];
    }
  });

  if (currentGroup.length > 0) {
    groupedData.push(currentGroup);
  }

  let paginatedData = [];
  let currentPageData = [];
  let count = 0;

  groupedData.forEach(group => {
    if (count + group.length > rowsPerPage) {
      paginatedData.push(currentPageData);
      currentPageData = [];
      count = 0;
    }
    currentPageData = currentPageData.concat(group);
    count += group.length;
  });

  if (currentPageData.length > 0) {
    paginatedData.push(currentPageData);
  }

  // Add pageIndex and itemIndex to each item
  paginatedData.forEach((pageData, pageIndex) => {
    pageData.forEach((item, itemIndex) => {
      item.pageIndex = pageIndex;
      item.itemIndex = itemIndex;
    });
  });

  return paginatedData;
};

// Function to update data
const updateData = (data, pageIndex, itemIndex, field, value) => {
  const newData = [...data];
  const globalIndex = newData.findIndex(item => item.pageIndex === pageIndex && item.itemIndex === itemIndex);
  newData[globalIndex][field] = value;
  return newData;
};

const EditableTable = ({ data, onSave }) => {
  const [editableData, setEditableData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [hideDone, setHideDone] = useState(false); // State for hiding "Done" rows
  const inputRefs = useRef([]);

  useEffect(() => {
    setEditableData(data);
  }, [data]);

  const maxGroupValue = () => {
    let max = 0;
    editableData.forEach(item => {
      if (item['Group ID'] > max) {
        max = Number(item['Group ID']);
      }
    });
    return max;
  };

  const handleChange = useCallback(
    debounce((pageIndex, itemIndex, field, value) => {
      console.log('handleChange:', pageIndex, itemIndex, field, value);
      const updatedData = updateData(editableData, pageIndex, itemIndex, field, value);
      setEditableData(updatedData);
      handleSave(pageIndex, itemIndex);
    }, 100),
    [editableData]
  );

  const handleBlur = useCallback(
    debounce((pageIndex, itemIndex, field, value) => {
      const updatedData = updateData(editableData, pageIndex, itemIndex, field, value);
      setEditableData(updatedData);
      handleSave(pageIndex, itemIndex);
    }, 300),
    [editableData]
  );

  const handleCheckboxChange = useCallback((pageIndex, itemIndex) => {
    setEditableData(prevData => {
      const newData = [...prevData];
      const globalIndex = newData.findIndex(item => item.pageIndex === pageIndex && item.itemIndex === itemIndex);
      newData[globalIndex].Excluded = !newData[globalIndex].Excluded;
      return newData;
    });
    handleSave(pageIndex, itemIndex);
  }, [editableData]);

  const handleSave = useCallback((pageIndex, itemIndex) => {
    const globalIndex = editableData.findIndex(item => item.pageIndex === pageIndex && item.itemIndex === itemIndex);
    const itemToSave = editableData[globalIndex];
    if (itemToSave) {
//      console.log('Saving data:', itemToSave);
      onSave(itemToSave);
    }
  }, [editableData, onSave]);

  const handleKeyDown = (e, pageIndex, itemIndex) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      const nextIndex = itemIndex + 1;
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex].focus();
      }
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      const prevIndex = itemIndex - 1;
      if (inputRefs.current[prevIndex]) {
        inputRefs.current[prevIndex].focus();
      }
    }
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const groupedDataWithPagination = useMemo(() => getPaginatedData(editableData, rowsPerPage), [editableData, rowsPerPage]);
  const paginatedData = groupedDataWithPagination[page] || [];

  // Generate page numbers for navigation links
  const totalPages = groupedDataWithPagination.length;


  return (
    <>
      <PaginationControls
        currentPage={page}
        totalPages={totalPages}
        onChangePage={handleChangePage}
      />
      <Box mb={2}>
        <Checkbox
          checked={hideDone}
          onChange={() => setHideDone(prev => !prev)}
        />
        <Typography variant="body1" component="span">Hide Done</Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Id</TableCell>
              <TableCell>Group ID</TableCell>
              <TableCell>Processed Image</TableCell>
              <TableCell>Source URL</TableCell>
              <TableCell>Card URL</TableCell>
              <TableCell>Excluded</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((item, itemIndex) => {
              if (hideDone && item['Card status'] === 'Done') {
                return null; // Skip this row if hiding "Done" items
              }
              const isNewGroup = itemIndex === 0 || paginatedData[itemIndex - 1]["Group field"] !== item["Group field"];
              const rowColor = getStatusColor(item['Card status'], item.Excluded);
              console.log(item)
              return (
                <React.Fragment key={item.Id}>
                  {isNewGroup && (
                    <TableRow key={`header-${item["Group field"]}`}>
                      <TableCell colSpan={8} style={{ backgroundColor: '#f0f0f0' }}>
                        <Typography variant="h5">{item["Group field"]}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow style={{ backgroundColor: rowColor }}>
                    <TableCell>{item.Name}</TableCell>
                    <TableCell>{item.Id}</TableCell>
                    <TableCell>
                      <TextField
                        value={item['Group ID']}
                        onChange={(e) => handleChange(page, itemIndex, 'Group ID', e.target.value)}
//                        onBlur={(e) => handleBlur(page, itemIndex, 'Group ID', e.target.value)}
//                        onKeyDown={(e) => handleKeyDown(e, page, itemIndex)}
                        onFocus={handleFocus}
                        variant="outlined"
                        size="small"
                        inputRef={(ref) => inputRefs.current[itemIndex] = ref}
                      />
                      <p>
                        <Button variant="contained" color="primary" onClick={() => handleChange(page, itemIndex, 'Group ID', maxGroupValue() + 1)}> new group </Button>
                        <Button variant="contained" style={{ backgroundColor: 'purple' }} onClick={() => handleChange(page, itemIndex, 'Group ID', maxGroupValue())}> last group </Button>
                      </p>
                    </TableCell>
                    <TableCell>
                      <ZoomableImage src={item.ProcessedImage} alt={item.Name} />
                    </TableCell>
                    <TableCell>
                      <Link href={item.SourceUrl} target="_blank" rel="noopener">
                        Link
                      </Link>
                    </TableCell>
                    <TableCell>
                      {item['Card URL'] ? (
                        <Link href={item['Card URL']} target="_blank" rel="noopener">
                          {item['Card status'] || 'N/A'}
                        </Link>
                      ) : (
                        item['Card status']
                      )}
                    </TableCell>
                    <TableCell>
                      <Checkbox
                        checked={item.Excluded && item.Excluded!="False" || false }
                        onChange={() => handleCheckboxChange(page, itemIndex)}
                      />
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginationControls
        currentPage={page}
        totalPages={totalPages}
        onChangePage={handleChangePage}
      />
    </>
  );
};

export default EditableTable;
